import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
} from "react";
import "./Rangeslider.css";
import { setRangeslider } from "../../redux/slice/Slice";
import { useDispatch } from "react-redux";

const debounce = (func, delay) => {
  let debounceTimer;
  return function (...args) {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => func.apply(this, args), delay);
  };
};

const INVESTMENT_RANGE = [
  { label: "10K - 1 LAC", value: 10000 },
  { label: "1 LAC - 3 LAC", value: 100000 },
  { label: "3 LAC - 10 LAC", value: 300000 },
  { label: "10 LAC - 20 LAC", value: 1000000 },
  { label: "20 LAC - 40 LAC", value: 2000000 },
  { label: "40 LAC - 80 LAC", value: 4000000 },
  { label: "80 LAC - 1 CR", value: 8000000 },
  { label: "MORE THAN 1 CR", value: 10000000 },
];

const formatValue = (value) => {
  if (value === 0) {
    return "0";
  } else if (value < 10000) {
    return `${value}`;
  } else if (value >= 10000 && value < 100000) {
    return `${(value / 1000).toFixed(0)}K`;
  } else if (value >= 100000 && value < 1000000) {
    return `${(value / 100000).toFixed(0)}lac`;
  } else if (value >= 1000000 && value < 10000000) {
    return `${(value / 100000).toFixed(0)} lac`;
  } else {
    return `${(value / 10000000).toFixed(0)} cr`;
  }
};

const Rangeslider = ({ min, max }) => {
  const dispatch = useDispatch();
  const [values, setValues] = useState({ min: min, max: max });
  const minValRef = useRef(min);
  const maxValRef = useRef(max);
  const range = useRef(null);

  const step = 10000; // Default step value

  const getNextStepValue = (value) => {
    for (let i = 0; i < INVESTMENT_RANGE.length; i++) {
      if (value < INVESTMENT_RANGE[i].value) {
        return INVESTMENT_RANGE[i].value;
      }
    }
    return value;
  };

  const getPercent = useMemo(
    () => (value) => Math.round(((value - min) / (max - min)) * 100),
    [min, max]
  );

  useEffect(() => {
    const minPercent = getPercent(values.min);
    const maxPercent = getPercent(values.max);

    if (range.current) {
      range.current.style.left = `${minPercent}%`;
      range.current.style.width = `${maxPercent - minPercent}%`;
    }
    dispatch(setRangeslider(values));
  }, [values.min, values.max, getPercent]);

  // const debouncedOnChange = useCallback(
  //   debounce((newValues) => onChange(newValues), 100),
  //   [onChange]
  // );

  // useEffect(() => {
  //   debouncedOnChange(values);
  // }, [values, debouncedOnChange]);

  const handleMinChange = useCallback(
    (event) => {
      const value = Number(event.target.value);
      const nextValue = getNextStepValue(value);
      setValues((prevValues) => ({
        ...prevValues,
        min: Math.min(nextValue, values.max - step),
      }));
      minValRef.current = nextValue; // Update minValRef
    },
    [values.max, step]
  );

  const handleMaxChange = useCallback(
    (event) => {
      const value = Number(event.target.value);
      const nextValue = getNextStepValue(value);
      setValues((prevValues) => ({
        ...prevValues,
        max: Math.max(nextValue, values.min + step),
      }));
      maxValRef.current = nextValue; // Update maxValRef
    },
    [values.min, step]
  );

  return (
    <div className="rangesliders">
      <input
        type="range"
        min={min}
        max={max}
        value={values.min}
        step={step}
        onChange={handleMinChange}
        className="thumb thumb--left"
        style={{ zIndex: values.min > max - 100 ? "5" : "3" }}
      />
      <input
        type="range"
        min={min}
        max={max}
        value={values.max}
        step={step}
        onChange={handleMaxChange}
        className="thumb thumb--right"
      />
      <div className="slider">
        <div className="slider__track" />
        <div ref={range} className="slider__range" />
        <div className="slider__left-value">
          <input
            value={formatValue(values.min)}
            min={min}
            max={max}
            style={{ width: "80px", height: "25px" }}
            onChange={handleMinChange}
          />
        </div>
        <div className="slider__right-value">
          <input
            value={formatValue(values.max)}
            min={min}
            max={max}
            style={{ width: "80px", height: "25px" }}
            onChange={handleMaxChange}
          />
        </div>
      </div>
    </div>
  );
};

export default Rangeslider;
